import { Fragment, useState, type JSX, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { MaterialSwatch } from "@/components/admin/MaterialPicker";
import { presetLabel } from "@/processes/dynamic/presetLabel";
import { cx } from "@/lib/utils";

/**
 * ModuleConfigForm — generic JSON Schema → form renderer.
 *
 * Per PR 2 schema-driven framework. Schema je
 * ``ProcessModuleDefinition.machine_config_schema`` /
 * ``b2c_presets_schema`` (loaded přes existing /admin/processes
 * endpoint).
 *
 * Supported field types (JSON Schema draft-07-ish subset):
 *   - number / integer  → numeric input
 *   - string            → text input (or select if ``enum`` set)
 *   - boolean           → checkbox
 *   - array of strings  → tag input (comma-separated text)
 *   - array of objects  → preset list (custom widget — values shown
 *                          as rows with up/down/remove)
 *
 * Specialized widgets dispatched přes ``widget`` field na property:
 *   - ``"preset_list"`` (default pro array of objects)
 *
 * Pokud schema chybí (modul nemá deklarované machine_config / b2c_presets),
 * tahle komponenta je JEDINÝ empty-state — žádný hardcoded per-process
 * fallback UI. Rendruje "schéma chybí — nastav v Platform admin" message
 * (admin schéma doplní v tabu Schémata).
 */

/** Rezervovaný klíč configu se skupinami pozic stroje (KAN-1201). */
export const POSITIONS_CONFIG_KEY = "positions";
/** Anotace „tuhle hodnotu plní platforma" a její jediná dnešní hodnota. */
export const DERIVED_PROP_KEY = "x-derived";
export const DERIVED_POSITION_COUNT = "position_count";

export type JsonSchema = {
  type?: string;
  title?: string;
  description?: string;
  properties?: Record<string, JsonSchema>;
  items?: JsonSchema;
  enum?: Array<string | number>;
  default?: unknown;
  minimum?: number;
  maximum?: number;
  unit?: string;
  widget?: string;
  required?: string[];
  /** Vlastnost, kterou plní platforma (dnes počet pozic) — formulář se na ni neptá. */
  "x-derived"?: string;
  /** Jednotné číslo jména pozice („Hlava" k titulku „Hlavy"). */
  "x-label-one"?: string;
  /**
   * Canonical machine-property link (platform-admin "Vlastnosti strojů").
   * When two operations' config properties share a ``machine_property_key``
   * they are the same physical property and the union machine form asks for
   * them once. Ignored by this renderer.
   */
  machine_property_key?: string;
  /**
   * Progressive disclosure BY VALUE (KAN-1144): render this property only
   * while another property of the same schema satisfies the condition.
   *
   * ``{key: "<jiný klíč configu>", op: "gt", value: 1}`` se čte „ptej se na
   * tohle, jen když je <jiný> větší než 1" — tak se stroje, který nese jednu
   * věc najednou, nikdo neptá, co stojí změna, aniž by kód platformy věděl,
   * co je změna nebo kapacita. Odkazovaný klíč je sourozenec v témže
   * (sjednoceném) schématu; neznámý klíč nebo chybějící hodnota znamená
   * „podmínka neplatí", takže nedosažitelný odkaz pole schová místo aby
   * ukázal takové, které nejde splnit.
   */
  applies_when?: {
    key: string;
    op: "gt" | "gte" | "lt" | "lte" | "eq" | "ne";
    value: number | string | boolean;
  };
  /**
   * Vazba pole na proveditelnost („tenhle rozměr stroje se porovnává s tímhle
   * faktem zakázky"). Tenhle renderer ji nečte — čte ji fit stroje a členění
   * kroku Konfigurace (``partitionConfigSections``), kterému říká, že pole
   * patří k otázce „co stroj unese".
   */
  feasibility?: { kind?: string; input?: string };
};

/**
 * Platí ``schema.applies_when`` nad ``config``? Bez deklarace vždycky.
 *
 * Sdílené se strojovým formulářem, který musí nesplněné pole vyhodit i
 * z validace povinných polí: skryté pole ponechané v ``required`` shodí
 * uložení chybou, která jmenuje něco, co tenant nevidí.
 */
export function appliesWhenSatisfied(
  schema: JsonSchema,
  config: Record<string, unknown>,
): boolean {
  const rule = schema.applies_when;
  if (!rule) return true;
  const actual = config[rule.key];
  if (actual === undefined || actual === null) return false;
  const { op, value } = rule;
  if (op === "eq") return actual === value;
  if (op === "ne") return actual !== value;
  // Uspořádání dává smysl jen mezi dvěma čísly; cokoli jiného (text
  // v číselném poli, boolean) je „neplatí", ne tichá koerce, kterou nikdo
  // nedeklaroval.
  const a = typeof actual === "number" ? actual : Number.NaN;
  const b = typeof value === "number" ? value : Number.NaN;
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  if (op === "gt") return a > b;
  if (op === "gte") return a >= b;
  if (op === "lt") return a < b;
  return a <= b;
}

export type ConfigValue = Record<string, unknown>;

/** One selectable material in the constrained "Podporované materiály" picker. */
export type MaterialOption = {
  code: string;
  label: string;
  swatchHex: string | null;
  swatchImageUrl: string | null;
};

export type ModuleConfigFormProps = {
  schema: JsonSchema | null | undefined;
  value: ConfigValue;
  onChange: (next: ConfigValue) => void;
  /** Test-id prefix pro nested test selectors. */
  testIdPrefix?: string;
  /**
   * When provided, the reserved ``materials`` array field renders as a
   * constrained multi-select over these options (values stored as material
   * codes) instead of the free comma-separated tag input. ``materials`` is
   * the same reserved config key the BE material gate reads
   * (``effective_materials_for_machine`` fallback), so the picker and the
   * decision share one source. The caller (machine form) computes the
   * options as ``tenant catalog ∩ platform envelope`` scoped to the
   * machine's operations. Absent / null → legacy tag input.
   */
  materialOptions?: MaterialOption[] | null;
  /**
   * Schéma JEDNÉ pozice (KAN-1201). Když ho volající předá, formulář se
   * nahoře zeptá na pozice a na odvozený počet už se neptá — jinak vypadá
   * přesně jako dřív. Tímhle jedním propem to dostanou všechny tři vchody:
   * dialog stroje, editor katalogu i Rozjezd.
   */
  positionSchema?: JsonSchema | null;
  /**
   * Kde se hodnota vzala, klíč po klíči (KAN-1151 / KAN-1152). Volitelný:
   * kdo ho nepředá, dostane formulář beze změny.
   *
   * ``inherited`` = přišlo z rodiče (katalogový základ u varianty,
   * katalogový stroj u tenanta) a nikdo to nepřepsal; ``overridden`` =
   * tahle vrstva hodnotu změnila.
   *
   * Skládá to VŽDY server (``/platform-machines/cards``, resp.
   * ``config_provenance`` u stroje tenanta). Dopočítávat původ v
   * prohlížeči by byla druhá implementace jednoho pravidla.
   */
  provenance?: Record<string, "inherited" | "overridden">;
  /** „Vrátit na zděděné" u přepsaného pole; bez něj se odznak jen ukáže. */
  onRevert?: (key: string) => void;
  /**
   * Co volající chce mít POD polem a jestli má být pole zamčené (KAN-1480).
   *
   * Dialog stroje tudy kreslí rady z katalogu a „zděděno · Přepsat": pole
   * s hodnotou z katalogu nejde přepsat omylem, dokud se neodemkne. Zamyká
   * se obalem ``<fieldset disabled>`` — ``Field`` se větví na půl tuctu
   * widgetů a protahovat ``readOnly`` každým z nich by znamenalo sáhnout do
   * všech; nativní fieldset vypne jakýkoli vstup uvnitř.
   */
  fieldDecor?: (key: string) => { footer?: ReactNode; locked?: boolean } | undefined;
};

export function ModuleConfigForm({
  schema,
  value,
  onChange,
  testIdPrefix = "module-config",
  materialOptions,
  provenance,
  onRevert,
  positionSchema,
  fieldDecor,
}: ModuleConfigFormProps): JSX.Element {
  const { t } = useTranslation("admin");
  if (!schema || !schema.properties) {
    return (
      <p
        className="text-xs text-ink-3"
        data-testid={`${testIdPrefix}-no-schema`}
      >
        {t("moduleConfig.noSchema", {
          defaultValue:
            "Tento modul nemá deklarované schéma — nastavte ho v Platform admin → Operace → Schémata.",
        })}
      </p>
    );
  }
  const updateField = (key: string, fieldValue: unknown) => {
    onChange({ ...value, [key]: fieldValue });
  };
  const hasPositions = Boolean(positionSchema?.properties);
  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      {schema.title && (
        <p className="text-xs font-semibold uppercase text-ink-3">
          {schema.title}
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {hasPositions && positionSchema && (
          <MachinePositionsField
            schema={positionSchema}
            value={value[POSITIONS_CONFIG_KEY]}
            onChange={(groups) => updateField(POSITIONS_CONFIG_KEY, groups)}
            testIdPrefix={`${testIdPrefix}-positions`}
          />
        )}
        {Object.entries(schema.properties)
          .filter(([, propSchema]) => appliesWhenSatisfied(propSchema, value))
          // Odvozené číslo se neptá dvakrát: má-li stroj pozice, počet je
          // jejich součet a pole by bylo druhé místo pro touž hodnotu.
          .filter(([, propSchema]) => !(hasPositions && propSchema[DERIVED_PROP_KEY] === DERIVED_POSITION_COUNT))
          .map(([key, propSchema]) => {
          const field = (
            <Field
              fieldKey={key}
              schema={propSchema}
              required={schema.required?.includes(key) ?? false}
              value={value[key]}
              onChange={(v) => updateField(key, v)}
              testIdPrefix={`${testIdPrefix}-${key}`}
              materialOptions={materialOptions}
            />
          );
          const decor = fieldDecor?.(key);
          if (decor) {
            return (
              <div key={key} className="min-w-0" data-testid={`${testIdPrefix}-${key}-decor`}>
                <fieldset
                  disabled={decor.locked ?? false}
                  className={decor.locked ? "min-w-0 [&_input]:bg-surface-2 [&_input]:text-ink-2 [&_select]:bg-surface-2" : "min-w-0"}
                >
                  {field}
                </fieldset>
                {decor.footer}
              </div>
            );
          }
          const origin = provenance?.[key];
          if (!origin) return <Fragment key={key}>{field}</Fragment>;
          return (
            <ProvenanceField
              key={key}
              origin={origin}
              testIdPrefix={`${testIdPrefix}-${key}`}
              onRevert={onRevert ? () => onRevert(key) : undefined}
            >
              {field}
            </ProvenanceField>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Jedno pole i s tím, odkud jeho hodnota je.
 *
 * ``role="group"`` + ``aria-label`` schválně místo ``aria-describedby`` na
 * vstupu: ``Field`` se větví na půl tuctu widgetů, každý si kreslí vstup
 * po svém, a protáhnout jedno id skrz všechny by znamenalo sáhnout do
 * každého z nich. Skupina původ oznámí při vstupu do pole a odznak ho
 * říká barvou i slovem — „rozlišitelné bez čtení popisku" tak platí i
 * vizuálně, ne jen pro odečítač.
 */
function ProvenanceField({
  origin,
  testIdPrefix,
  onRevert,
  children,
}: {
  origin: "inherited" | "overridden";
  testIdPrefix: string;
  onRevert?: () => void;
  children: ReactNode;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const inherited = origin === "inherited";
  const label = inherited
    ? t("moduleConfig.provenanceInherited", { defaultValue: "zděděno" })
    : t("moduleConfig.provenanceOverridden", { defaultValue: "přepsáno" });
  return (
    <div
      role="group"
      aria-label={label}
      className={inherited ? "opacity-70" : undefined}
      data-testid={`${testIdPrefix}-provenance`}
      data-provenance={origin}
    >
      <div className="mb-1 flex items-center gap-2">
        <span
          className={
            inherited
              ? "rounded bg-surface-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-ink-3"
              : "rounded bg-accent-bg px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent"
          }
        >
          {label}
        </span>
        {!inherited && onRevert && (
          <button
            type="button"
            onClick={onRevert}
            className="text-[11px] text-accent underline underline-offset-2"
            data-testid={`${testIdPrefix}-revert`}
          >
            {t("moduleConfig.revertToInherited", {
              defaultValue: "vrátit na zděděné",
            })}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/** Consistent required marker for fields declared in the JSON schema. */
function RequiredMark(): JSX.Element {
  const { t } = useTranslation("admin");
  return (
    <abbr
      title={t("machines.requiredField", { defaultValue: "Povinné pole" })}
      aria-label={t("machines.requiredField", { defaultValue: "Povinné pole" })}
      className="ml-0.5 text-danger no-underline"
      data-testid="module-config-required-mark"
    >
      *
    </abbr>
  );
}

function Field({
  fieldKey,
  schema,
  required = false,
  value,
  onChange,
  testIdPrefix,
  materialOptions,
}: {
  fieldKey: string;
  schema: JsonSchema;
  required?: boolean;
  value: unknown;
  onChange: (v: unknown) => void;
  testIdPrefix: string;
  materialOptions?: MaterialOption[] | null;
}): JSX.Element {
  const label = schema.title ?? fieldKey;
  const description = schema.description;
  const type = schema.type ?? "string";
  // Kapacita jako volba, ne číselník (KAN-1144). Widget nastavuje volající
  // podle DEKLAROVANÝCH dat (POČET, který fit stroje porovnává řádkem
  // ``limits`` s potřebou zakázky — viz ``partitionConfigSections``), ne
  // podle jména klíče — u frézky se táž otázka jmenuje „Nástrojů
  // v zásobníku", u vyšívačky „Jehel".
  if (schema.widget === "slots") {
    return (
      <SlotsField
        label={label}
        description={description}
        schema={schema}
        required={required}
        value={value}
        onChange={onChange}
        testIdPrefix={testIdPrefix}
      />
    );
  }
  // Tunable customer option — one coherent widget bundling presets +
  // default (picked from those presets) + allow-slider toggle.
  if (schema.widget === "tunable") {
    return (
      <TunableField
        label={label}
        description={description}
        schema={schema}
        required={required}
        value={value}
        onChange={onChange}
        testIdPrefix={testIdPrefix}
      />
    );
  }
  // Constrained material list: the reserved ``materials`` array field
  // rendered as a multi-select over the supplied options (catalog ∩
  // platform envelope) instead of the free tag input — the same reserved
  // config key the BE material gate falls back to. Only kicks in when the
  // caller actually provides ``materialOptions`` (machine form); other
  // callers / schemas keep the legacy tag input below.
  if (type === "array" && fieldKey === "materials" && materialOptions) {
    return (
      <MaterialMultiSelectField
        label={label}
        description={description}
        options={materialOptions}
        required={required}
        value={Array.isArray(value) ? (value as string[]) : []}
        onChange={(v) => onChange(v)}
        testIdPrefix={testIdPrefix}
      />
    );
  }
  // Specialized widgets first (preset_list array of objects).
  if (schema.widget === "preset_list" || (type === "array" && schema.items?.type === "object")) {
    return (
      <PresetListField
        label={label}
        description={description}
        itemSchema={schema.items}
        required={required}
        value={Array.isArray(value) ? (value as Record<string, unknown>[]) : []}
        onChange={(v) => onChange(v)}
        testIdPrefix={testIdPrefix}
      />
    );
  }
  if (type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-sm md:col-span-2">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={`${testIdPrefix}-checkbox`}
        />
        <span>
          <span className="font-medium">
            {label}
            {required && <RequiredMark />}
          </span>
          {description && (
            <span className="ml-2 text-xs text-ink-3">{description}</span>
          )}
        </span>
      </label>
    );
  }
  if (Array.isArray(schema.enum) && type !== "array") {
    return (
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">
          {label}
          {required && <RequiredMark />}
          {schema.unit && (
            <span className="ml-1 text-xs font-normal text-ink-3">
              ({schema.unit})
            </span>
          )}
        </span>
        <select
          value={value !== undefined && value !== null ? String(value) : ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded border border-border-strong px-2 py-1.5"
          data-testid={`${testIdPrefix}-select`}
        >
          <option value="">—</option>
          {schema.enum.map((opt) => (
            <option key={String(opt)} value={String(opt)}>
              {String(opt)}
            </option>
          ))}
        </select>
        {description && (
          <span className="text-xs text-ink-3">{description}</span>
        )}
      </label>
    );
  }
  if (type === "array" && (!schema.items || schema.items.type === "string")) {
    // Array of strings → comma-separated tag input.
    const arr = Array.isArray(value) ? (value as string[]) : [];
    return (
      <label className="flex flex-col gap-1 text-sm md:col-span-2">
        <span className="font-medium">
          {label}
          {required && <RequiredMark />}
        </span>
        <input
          type="text"
          value={arr.join(", ")}
          placeholder="A, B, C"
          onChange={(e) => {
            const parts = e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            onChange(parts);
          }}
          className="w-full rounded border border-border-strong px-2 py-1.5"
          data-testid={`${testIdPrefix}-tags`}
        />
        {description && (
          <span className="text-xs text-ink-3">{description}</span>
        )}
      </label>
    );
  }
  if (type === "number" || type === "integer") {
    const numStr = value === null || value === undefined || value === "" ? "" : String(value);
    return (
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">
          {label}
          {required && <RequiredMark />}
          {schema.unit && (
            <span className="ml-1 text-xs font-normal text-ink-3">
              ({schema.unit})
            </span>
          )}
        </span>
        <input
          type="number"
          min={schema.minimum}
          max={schema.maximum}
          step={type === "integer" ? "1" : "any"}
          value={numStr}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange(null);
              return;
            }
            const n = type === "integer" ? Number.parseInt(raw, 10) : Number.parseFloat(raw);
            if (Number.isFinite(n)) onChange(n);
          }}
          className="w-full rounded border border-border-strong px-2 py-1.5"
          data-testid={`${testIdPrefix}-number`}
        />
        {description && (
          <span className="text-xs text-ink-3">{description}</span>
        )}
      </label>
    );
  }
  // Default: text input.
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">
        {label}
        {required && <RequiredMark />}
      </span>
      <input
        type="text"
        value={value === null || value === undefined ? "" : String(value)}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-border-strong px-2 py-1.5"
        data-testid={`${testIdPrefix}-text`}
      />
      {description && (
        <span className="text-xs text-ink-3">{description}</span>
      )}
    </label>
  );
}

/**
 * „Kolik věcí stroj nese v jedné práci" — volba, ne číselník (KAN-1144).
 *
 * Nabídnuté hodnoty jsou 1 · 2 · 5 a „jiné"; tři nejčastější odpovědi zabere
 * jeden klik a cokoli dalšího se dopíše. Čtverečky pod volbou ukazují KOLIK,
 * ne CO — přiřazení konkrétního materiálu do slotu se nikde nevede a
 * kreslit ho by slibovalo nastavení, které neexistuje.
 *
 * ``min`` / ``max`` ze schématu se respektují: nabídka se ořízne na to, co
 * autor dovolil, takže stroj s deklarovaným maximem 2 nedostane volbu 5.
 */
function SlotsField({
  label,
  description,
  schema,
  required,
  value,
  onChange,
  testIdPrefix,
}: {
  label: string;
  description?: string;
  schema: JsonSchema;
  required: boolean;
  value: unknown;
  onChange: (v: unknown) => void;
  testIdPrefix: string;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const min = typeof schema.minimum === "number" ? schema.minimum : 1;
  const max = typeof schema.maximum === "number" ? schema.maximum : undefined;
  const current = typeof value === "number" ? value : undefined;
  const presets = [1, 2, 5].filter((n) => n >= min && (max === undefined || n <= max));
  // „Jiné" je stav vstupu, ne uložená hodnota: otevře ho buď kliknutí, nebo
  // to, že uložené číslo mezi nabídkami není (stroj s osmi hlavami musí své
  // číslo VIDĚT, ne dostat nejbližší preset).
  const [custom, setCustom] = useState(
    current !== undefined && !presets.includes(current),
  );
  // Sloty kreslíme jen pro rozumný počet — u stroje s 200 pozicemi je dvě stě
  // čtverečků šum, ne informace.
  const slots = current !== undefined && current > 1 && current <= 12 ? current : 0;
  return (
    <div data-testid={testIdPrefix}>
      <label className="mb-1 block text-xs font-medium text-ink-2">
        {label}
        {required && <RequiredMark />}
      </label>
      {description && <p className="mb-1 text-[11px] text-ink-3">{description}</p>}
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
        {presets.map((n) => {
          const on = !custom && current === n;
          return (
            <button
              key={n}
              type="button"
              aria-pressed={on}
              data-testid={`${testIdPrefix}-preset-${n}`}
              onClick={() => {
                setCustom(false);
                onChange(n);
              }}
              className={cx(
                "rounded-md border px-3 py-1 text-sm tabular-nums",
                on
                  ? "border-accent bg-accent-bg font-semibold text-accent"
                  : "border-border bg-surface text-ink-2",
              )}
            >
              {n}
            </button>
          );
        })}
        <button
          type="button"
          aria-pressed={custom}
          data-testid={`${testIdPrefix}-preset-other`}
          onClick={() => setCustom(true)}
          className={cx(
            "rounded-md border px-3 py-1 text-sm",
            custom
              ? "border-accent bg-accent-bg font-semibold text-accent"
              : "border-border bg-surface text-ink-2",
          )}
        >
          {t("moduleConfig.slotsOther", { defaultValue: "jiné" })}
        </button>
      </div>
      {custom && (
        <input
          type="number"
          min={min}
          max={max}
          step={1}
          value={current ?? ""}
          data-testid={`${testIdPrefix}-number`}
          className="mt-1.5 w-24 rounded-md border border-border bg-surface px-2 py-1 text-sm tabular-nums"
          onChange={(e) => {
            const next = e.target.value;
            onChange(next === "" ? undefined : Number(next));
          }}
        />
      )}
      {slots > 0 && (
        <div
          className="mt-1.5 flex flex-wrap gap-1"
          aria-hidden="true"
          data-testid={`${testIdPrefix}-slots`}
        >
          {Array.from({ length: slots }, (_, i) => (
            <span
              key={i}
              className="flex h-6 w-6 items-center justify-center rounded border border-accent text-[10px] font-semibold text-accent"
            >
              {i + 1}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export type PositionGroup = { count: number } & Record<string, unknown>;

/** Skupiny pozic z uložené hodnoty — cokoli jiného než seznam objektů je „žádné". */
export function readPositionGroups(value: unknown): PositionGroup[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((g): g is Record<string, unknown> => Boolean(g) && typeof g === "object")
    .map((g) => ({ ...g, count: typeof g.count === "number" && g.count > 0 ? Math.floor(g.count) : 1 }));
}

const POSITION_STRIP_MAX = 24;

/**
 * Pozice stroje: kolik jich je a jestli jsou stejné (KAN-1203).
 *
 * Běžný stroj má pozice stejné, takže se odpovídá dvěma klikům a formulář
 * vypadá jako dřív. Teprve „Ne" rozbalí skupiny — a i tam se počet POSLEDNÍ
 * skupiny dopočítá ze zbytku, aby uživatel nikdy nepsal číslo, které jde
 * odvodit. Součet skupin je jediné číslo o počtu pozic; kapacita se z něj
 * odvozuje na serveru, žádné druhé pole.
 *
 * Jména („Hlavy", „Kapsy zásobníku") přicházejí ze schématu operace;
 * komponenta žádné neví.
 */
function MachinePositionsField({
  schema,
  value,
  onChange,
  testIdPrefix,
}: {
  schema: JsonSchema;
  value: unknown;
  onChange: (groups: PositionGroup[]) => void;
  testIdPrefix: string;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const stored = readPositionGroups(value);
  // Stroj, který skupiny ještě nemá, se chová jako jedna skupina s jednou
  // pozicí: vlastnosti musí být vidět HNED, jinak by výchozí odpověď „všechny
  // stejné" nešla vyplnit, dokud uživatel nesáhne na počet.
  const groups = stored.length > 0 ? stored : [{ count: 1 } as PositionGroup];
  const label = schema.title || t("moduleConfig.positions.label");
  const total = groups.reduce((sum, g) => sum + g.count, 0) || 1;
  const same = groups.length <= 1;
  const properties = Object.entries(schema.properties ?? {});
  const presets = [1, 2, 5];
  const [custom, setCustom] = useState(!presets.includes(total));

  const setTotal = (next: number) => {
    const wanted = Math.max(1, Math.floor(next));
    const base = groups.length > 0 ? groups : [{ count: 1 } as PositionGroup];
    if (base.length === 1) {
      onChange([{ ...base[0], count: wanted }]);
      return;
    }
    // Víc skupin: mění se POSLEDNÍ, protože ta bere zbytek. Kdyby na ni
    // nezbylo, ubírají se skupiny od konce — s nulou žádná nevznikne.
    const kept: PositionGroup[] = [];
    let left = wanted;
    for (const group of base.slice(0, -1)) {
      if (left <= 1) break;
      const count = Math.min(group.count, left - 1);
      kept.push({ ...group, count });
      left -= count;
    }
    kept.push({ ...base[base.length - 1], count: Math.max(1, left) });
    onChange(kept);
  };

  const setSame = (nextSame: boolean) => {
    if (nextSame) {
      onChange([{ ...groups[0], count: total }]);
      return;
    }
    const first = groups[0] ?? ({ count: total } as PositionGroup);
    if (total < 2) {
      onChange([{ ...first, count: 1 }, { ...first, count: 1 }]);
      return;
    }
    onChange([
      { ...first, count: 1 },
      { ...first, count: total - 1 },
    ]);
  };

  const patchGroup = (index: number, patch: Record<string, unknown>) => {
    const next = groups.map((group, i) => (i === index ? { ...group, ...patch } : group));
    onChange(
      next.map((group, i, all) =>
        i === all.length - 1
          ? { ...group, count: Math.max(1, total - all.slice(0, -1).reduce((s, g) => s + g.count, 0)) }
          : group,
      ),
    );
  };

  const splitAt = (position: number) => {
    // Políčko se odděluje do vlastní skupiny: skupina, do které patří, se
    // zmenší o jednu pozici a hned za ni přibude nová s jednou.
    let seen = 0;
    const next: PositionGroup[] = [];
    for (const group of groups) {
      if (position >= seen && position < seen + group.count && group.count > 1) {
        next.push({ ...group, count: group.count - 1 }, { ...group, count: 1 });
      } else {
        next.push(group);
      }
      seen += group.count;
    }
    onChange(next);
  };

  const stripColours = ["border-accent text-accent", "border-warn text-warn", "border-ok text-ok"];
  const strip: { group: number }[] = [];
  groups.forEach((group, index) => {
    for (let i = 0; i < group.count && strip.length < POSITION_STRIP_MAX; i += 1) {
      strip.push({ group: index });
    }
  });

  return (
    <div className="md:col-span-2" data-testid={testIdPrefix}>
      <label className="mb-1 block text-xs font-medium text-ink-2">{label}</label>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
        {presets.map((n) => (
          <button
            key={n}
            type="button"
            aria-pressed={!custom && total === n}
            data-testid={`${testIdPrefix}-preset-${n}`}
            onClick={() => {
              setCustom(false);
              setTotal(n);
            }}
            className={cx(
              "rounded-md border px-3 py-1 text-sm tabular-nums",
              !custom && total === n
                ? "border-accent bg-accent-bg font-semibold text-accent"
                : "border-border bg-surface text-ink-2",
            )}
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={custom}
          data-testid={`${testIdPrefix}-preset-other`}
          onClick={() => setCustom(true)}
          className={cx(
            "rounded-md border px-3 py-1 text-sm",
            custom
              ? "border-accent bg-accent-bg font-semibold text-accent"
              : "border-border bg-surface text-ink-2",
          )}
        >
          {t("moduleConfig.slotsOther", { defaultValue: "jiné" })}
        </button>
        {custom && (
          <input
            type="number"
            min={1}
            step={1}
            value={total}
            data-testid={`${testIdPrefix}-number`}
            className="w-24 rounded-md border border-border bg-surface px-2 py-1 text-sm tabular-nums"
            onChange={(e) => setTotal(Number(e.target.value || 1))}
          />
        )}
      </div>
      <p className="mt-2 text-xs font-medium text-ink-2">
        {t("moduleConfig.positions.same")}
      </p>
      <div className="mt-1 flex gap-1.5" role="group" aria-label={t("moduleConfig.positions.same")}>
        {[true, false].map((option) => (
          <button
            key={String(option)}
            type="button"
            aria-pressed={same === option}
            data-testid={`${testIdPrefix}-same-${option ? "yes" : "no"}`}
            onClick={() => setSame(option)}
            className={cx(
              "rounded-md border px-3 py-1 text-sm",
              same === option
                ? "border-accent bg-accent-bg font-semibold text-accent"
                : "border-border bg-surface text-ink-2",
            )}
          >
            {option ? t("moduleConfig.positions.yes") : t("moduleConfig.positions.no")}
          </button>
        ))}
      </div>
      <div className="mt-2 space-y-2">
        {groups.map((group, index) => (
          <div
            key={index}
            className="rounded-md border border-border bg-surface p-2"
            data-testid={`${testIdPrefix}-group-${index}`}
          >
            <p className="mb-1 text-[11px] text-ink-3">
              {index === groups.length - 1 && groups.length > 1
                ? t("moduleConfig.positions.groupRest", { count: group.count })
                : t("moduleConfig.positions.group", { count: group.count })}
            </p>
            {!same && index < groups.length - 1 && (
              <input
                type="number"
                min={1}
                step={1}
                value={group.count}
                aria-label={t("moduleConfig.positions.count")}
                data-testid={`${testIdPrefix}-group-${index}-count`}
                className="mb-1 w-20 rounded-md border border-border bg-surface px-2 py-1 text-sm tabular-nums"
                onChange={(e) => patchGroup(index, { count: Math.max(1, Number(e.target.value || 1)) })}
              />
            )}
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {properties.map(([key, propSchema]) => (
                <Field
                  key={key}
                  fieldKey={key}
                  schema={propSchema}
                  required={false}
                  value={group[key]}
                  onChange={(v) => patchGroup(index, { [key]: v })}
                  testIdPrefix={`${testIdPrefix}-group-${index}-${key}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {strip.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-1" data-testid={`${testIdPrefix}-strip`}>
          {strip.map((cell, index) => (
            <button
              key={index}
              type="button"
              title={t("moduleConfig.positions.split")}
              aria-label={t("moduleConfig.positions.cell", {
                index: index + 1,
                total: strip.length,
                group: cell.group + 1,
              })}
              data-testid={`${testIdPrefix}-cell-${index}`}
              onClick={() => splitAt(index)}
              className={cx(
                "flex h-6 w-6 items-center justify-center rounded border text-[10px] font-semibold",
                stripColours[cell.group % stripColours.length],
              )}
            >
              {index + 1}
            </button>
          ))}
        </div>
      )}
      <p className="mt-1 text-[11px] text-ink-3">{t("moduleConfig.positions.hint")}</p>
    </div>
  );
}

function MaterialMultiSelectField({
  label,
  description,
  options,
  required = false,
  value,
  onChange,
  testIdPrefix,
}: {
  label: string;
  description?: string;
  options: MaterialOption[];
  required?: boolean;
  value: string[];
  onChange: (v: string[]) => void;
  testIdPrefix: string;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const isSelected = (code: string): boolean =>
    value.some((v) => v.toLowerCase() === code.toLowerCase());
  const toggle = (code: string): void => {
    if (isSelected(code)) {
      onChange(value.filter((v) => v.toLowerCase() !== code.toLowerCase()));
    } else {
      onChange([...value, code]);
    }
  };
  // Codes saved earlier (legacy free-text input) that no longer match any
  // allowed option — surfaced as removable chips so a save doesn't
  // silently drop them, but they can't be re-added once removed.
  const stale = value.filter(
    (v) => !options.some((o) => o.code.toLowerCase() === v.toLowerCase()),
  );
  return (
    <fieldset
      className="md:col-span-2 rounded border border-border bg-surface p-3"
      data-testid={`${testIdPrefix}-material-multiselect`}
    >
      <legend className="px-1 text-xs font-semibold uppercase text-ink-3">
        {label}
        {required && <RequiredMark />}
      </legend>
      {description && <p className="mt-1 text-xs text-ink-3">{description}</p>}
      {options.length === 0 ? (
        <p className="mt-2 text-xs text-ink-3" data-testid={`${testIdPrefix}-empty`}>
          {t("moduleConfig.materials.empty", {
            defaultValue:
              "Pro tuto operaci zatím není povolený žádný materiál. Přidejte ho v Sklad a nákup → Položky a přiřaďte mu tuto operaci.",
          })}
        </p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {options.map((opt) => {
            const active = isSelected(opt.code);
            return (
              <button
                key={opt.code}
                type="button"
                onClick={() => toggle(opt.code)}
                aria-pressed={active}
                className={
                  active
                    ? "flex items-center gap-1.5 rounded-full border border-ink bg-ink px-2 py-1 text-xs text-bg"
                    : "flex items-center gap-1.5 rounded-full border border-border-strong bg-bg px-2 py-1 text-xs text-ink hover:bg-surface-2"
                }
                data-testid={`${testIdPrefix}-option-${opt.code}`}
              >
                <MaterialSwatch
                  swatchHex={opt.swatchHex}
                  swatchImageUrl={opt.swatchImageUrl}
                  alt={opt.code}
                />
                <span className="font-medium">{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
      {stale.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase text-warn">
            {t("moduleConfig.materials.staleLabel", {
              defaultValue: "Mimo povolený seznam:",
            })}
          </span>
          {stale.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() =>
                onChange(value.filter((v) => v.toLowerCase() !== code.toLowerCase()))
              }
              className="flex items-center gap-1 rounded-full border border-warn-border bg-warn-bg px-2 py-1 text-xs text-warn"
              data-testid={`${testIdPrefix}-stale-${code}`}
            >
              <span>{code}</span>
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}
    </fieldset>
  );
}

/**
 * Popisky sloupců presetu (KAN-1038).
 *
 * ``value`` / ``label_cs`` / ``label_en`` jsou REZERVOVANÉ klíče frameworku —
 * ``_tunable_property`` je generuje bez ``title`` a ``TunableField`` je čte
 * jménem. Bez téhle mapy jim ``Field`` udělá popisek z holého klíče a
 * tenantovi se v dialogu stroje ukázala hlavička ``value / label_cs /
 * label_en``. Vlastní ``title`` ve schématu má přednost — tohle je záchranná
 * síť pro odvozený tvar, ne přepis.
 */
function useReservedPresetTitle(): (key: string) => string | undefined {
  const { t } = useTranslation("admin");
  return (key: string): string | undefined => {
    switch (key) {
      case "value":
        return t("moduleConfig.presetColumn.value", { defaultValue: "Hodnota" });
      case "label_cs":
        return t("moduleConfig.presetColumn.labelCs", {
          defaultValue: "Název pro zákazníka (česky)",
        });
      case "label_en":
        return t("moduleConfig.presetColumn.labelEn", {
          defaultValue: "Název pro zákazníka (anglicky)",
        });
      default:
        return undefined;
    }
  };
}

function PresetListField({
  label,
  description,
  itemSchema,
  required = false,
  value,
  onChange,
  testIdPrefix,
  unit,
}: {
  label: string;
  description?: string;
  itemSchema: JsonSchema | undefined;
  required?: boolean;
  value: Record<string, unknown>[];
  onChange: (v: Record<string, unknown>[]) => void;
  testIdPrefix: string;
  /** Jednotka tunable pole — dědí ji sloupec ``value``, aby tenant u „50"
   *  viděl, jestli plní procenta nebo milimetry (KAN-1038). */
  unit?: string;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const reservedTitle = useReservedPresetTitle();
  const itemProps = itemSchema?.properties ?? {};
  const itemKeys = Object.keys(itemProps);
  const columnSchema = (key: string): JsonSchema => {
    const base = itemProps[key];
    const title = base.title ?? reservedTitle(key);
    const columnUnit = base.unit ?? (key === "value" ? unit || undefined : undefined);
    if (title === base.title && columnUnit === base.unit) return base;
    return { ...base, title, unit: columnUnit };
  };

  const addRow = () => {
    const blank: Record<string, unknown> = {};
    for (const key of itemKeys) {
      const propSchema = itemProps[key];
      if (propSchema.default !== undefined) blank[key] = propSchema.default;
    }
    blank.sort_order = value.length;
    onChange([...value, blank]);
  };
  const removeRow = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };
  const moveRow = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= value.length) return;
    const next = value.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  const updateRow = (idx: number, key: string, v: unknown) => {
    const next = value.slice();
    next[idx] = { ...next[idx], [key]: v };
    onChange(next);
  };

  return (
    <fieldset
      className="md:col-span-2 rounded border border-border bg-surface p-3"
      data-testid={`${testIdPrefix}-preset-list`}
    >
      <legend className="flex items-center justify-between px-1 text-xs font-semibold uppercase text-ink-3">
        <span>
          {label}
          {required && <RequiredMark />}
        </span>
        <button
          type="button"
          onClick={addRow}
          className="rounded border border-border px-2 py-0.5 text-xs hover:bg-surface-2"
          data-testid={`${testIdPrefix}-preset-add`}
        >
          {t("moduleConfig.presetAdd", { defaultValue: "Přidat preset" })}
        </button>
      </legend>
      {description && (
        <p className="mt-1 text-xs text-ink-3">{description}</p>
      )}
      {value.length === 0 ? (
        <p className="mt-2 text-xs text-ink-3">
          {t("moduleConfig.presetEmpty", {
            defaultValue: "Žádné presety.",
          })}
        </p>
      ) : (
        <div className="mt-2 space-y-1">
          {value.map((row, idx) => (
            <div
              key={idx}
              className="grid grid-cols-1 items-end gap-2 md:grid-cols-[1fr_1fr_1fr_auto]"
              data-testid={`${testIdPrefix}-preset-row-${idx}`}
            >
              {itemKeys
                .filter((key) => key !== "sort_order")
                .map((key) => (
                  <Field
                    key={key}
                    fieldKey={key}
                    schema={columnSchema(key)}
                    value={row[key]}
                    onChange={(v) => updateRow(idx, key, v)}
                    testIdPrefix={`${testIdPrefix}-preset-${idx}-${key}`}
                  />
                ))}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveRow(idx, -1)}
                  disabled={idx === 0}
                  className="rounded border border-border px-2 py-1 text-xs disabled:opacity-30"
                  aria-label={t("moduleConfig.presetMoveUp", { defaultValue: "Posunout nahoru" })}
                  data-testid={`${testIdPrefix}-preset-up-${idx}`}
                >
                  <span aria-hidden="true">↑</span>
                </button>
                <button
                  type="button"
                  onClick={() => moveRow(idx, 1)}
                  disabled={idx === value.length - 1}
                  className="rounded border border-border px-2 py-1 text-xs disabled:opacity-30"
                  aria-label={t("moduleConfig.presetMoveDown", { defaultValue: "Posunout dolů" })}
                  data-testid={`${testIdPrefix}-preset-down-${idx}`}
                >
                  <span aria-hidden="true">↓</span>
                </button>
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  className="rounded border border-border px-2 py-1 text-xs text-danger hover:bg-surface-2"
                  aria-label={t("moduleConfig.presetRemove", { defaultValue: "Odebrat řádek" })}
                  data-testid={`${testIdPrefix}-preset-remove-${idx}`}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </fieldset>
  );
}

function TunableField({
  label,
  description,
  schema,
  required = false,
  value,
  onChange,
  testIdPrefix,
}: {
  label: string;
  description?: string;
  schema: JsonSchema;
  required?: boolean;
  value: unknown;
  onChange: (v: unknown) => void;
  testIdPrefix: string;
}): JSX.Element {
  const { t, i18n } = useTranslation("admin");
  const obj =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const presetsSchema = schema.properties?.presets;
  const valueType =
    schema.properties?.default?.type === "integer" ? "integer" : "number";
  const unit = schema.unit;
  const presets = Array.isArray(obj.presets)
    ? (obj.presets as Record<string, unknown>[])
    : [];
  const defaultVal = obj.default;
  const allowSlider = Boolean(obj.allow_slider);
  const update = (patch: Record<string, unknown>) =>
    onChange({ ...obj, ...patch });

  // Default is picked FROM the tenant's presets — so the two can't drift.
  // Nabídka nese TENANTŮV název presetu a hodnotu s jednotkou, ne holé číslo:
  // „50" samo o sobě neřekne, jestli je to procento výplně nebo milimetr
  // vrstvy, a je to přesně ten text, který uvidí zákazník (KAN-1038, KAN-1041).
  const presetValues = presets
    .map((row) => row.value)
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  const valueOptions = presets
    .filter((row) => typeof row.value === "number")
    .map((row) => ({
      value: row.value as number,
      label: presetLabel(row, i18n.language, unit, presetValues),
    }))
    .filter(
      (opt, idx, all) => all.findIndex((o) => o.value === opt.value) === idx,
    );

  return (
    <fieldset
      className="md:col-span-2 rounded border border-border bg-surface p-3"
      data-testid={`${testIdPrefix}-tunable`}
    >
      <legend className="px-1 text-xs font-semibold uppercase text-ink-3">
        {label}
        {required && <RequiredMark />}
        {unit && <span className="ml-1 normal-case text-ink-4">({unit})</span>}
      </legend>
      {description && <p className="mt-1 text-xs text-ink-3">{description}</p>}
      <div className="mt-2 space-y-3">
        <PresetListField
          label={t("moduleConfig.tunablePresets", { defaultValue: "Presety" })}
          itemSchema={presetsSchema?.items}
          value={presets}
          onChange={(v) => update({ presets: v })}
          testIdPrefix={`${testIdPrefix}-presets`}
          unit={unit}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">
              {t("moduleConfig.tunableDefault", {
                defaultValue: "Výchozí hodnota",
              })}
            </span>
            <select
              value={typeof defaultVal === "number" ? String(defaultVal) : ""}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  update({ default: null });
                  return;
                }
                const n =
                  valueType === "integer"
                    ? Number.parseInt(raw, 10)
                    : Number.parseFloat(raw);
                update({ default: Number.isFinite(n) ? n : null });
              }}
              className="w-full rounded border border-border-strong px-2 py-1.5"
              data-testid={`${testIdPrefix}-default`}
            >
              <option value="">—</option>
              {valueOptions.map((opt) => (
                <option key={String(opt.value)} value={String(opt.value)}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-start gap-2 text-sm md:mt-6">
            <input
              type="checkbox"
              checked={allowSlider}
              onChange={(e) => update({ allow_slider: e.target.checked })}
              data-testid={`${testIdPrefix}-slider`}
            />
            <span>
              {t("moduleConfig.tunableSlider", {
                defaultValue:
                  "Povolit zákazníkovi slider (mezi nejnižším a nejvyšším presetem)",
              })}
            </span>
          </label>
        </div>
      </div>
    </fieldset>
  );
}
